import { useState } from 'react';
import { Button, Spinner } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';

import {
  APP_EVENTS,
  captureAppEvent,
  MARK_COMPLETE_VIA,
} from 'src/services/posthogEvents';

import useRemoteBoundStore from 'src/store/useRemoteBoundStore';

import { Api } from 'src/models/Api.model';
import { CriteriaGroupGroupElementsInnerOperatorEnum } from 'src/models/GalenData/CriteriaGroupGroupElementsInner.model';
import { OrderStatusEnum } from 'src/models/GalenData/Order.model';
import { I18NextNS } from 'src/models/Language.model';

interface CloseOrderButtonProps {
  onOrderComplete: () => void;
}

const CloseOrderButton = ({ onOrderComplete }: CloseOrderButtonProps) => {
  const { session } = useRemoteBoundStore();
  const { order, orderId } = session;

  const isSharedWithProvider = order?.IsSharedWithProvider?.value;
  const isSavedToChart = order?.IsSavedToChart?.value;

  const allTasksComplete = isSharedWithProvider && isSavedToChart;

  const {
    t,
    i18n: { language },
  } = useTranslation(I18NextNS.COMMON);

  const [isProcessing, setIsProcessing] = useState(false);

  const handleClick = async () => {
    try {
      if (!orderId) {
        throw new Error(t('message.error.orderNotFound'));
      }

      setIsProcessing(true);

      const orderData = await Api.remote.get_exam_order({
        groupElements: [
          {
            key: 'OrderId',
            operator: CriteriaGroupGroupElementsInnerOperatorEnum.Equal,
            value: orderId,
          },
        ],
      });

      if (!orderData.data.content?.[0]) {
        throw new Error(t('message.error.orderNotFound'));
      }

      await Api.remote.noRetry.update_exam_order(orderData.data.content[0], {
        status: OrderStatusEnum.COMPLETE,
      });

      captureAppEvent(APP_EVENTS.ORDER_MARKED_AS_COMPLETE, {
        order_id: orderId,
        via: MARK_COMPLETE_VIA.REPORT,
      });

      onOrderComplete();
    } catch (error) {
      Api.alertBox({
        title: t('bootbox.title.Error'),
        message: Api.formatException((error as Error).message),
        locale: language,
        type: 'error',
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const isDisabled = !allTasksComplete || isProcessing;

  return (
    <div>
      <Button
        onClick={handleClick}
        disabled={isDisabled}
        data-test="close-order-button"
        data-track="results.order-tasks.close-order"
        data-observe
      >
        {isProcessing && (
          <Spinner as="span" animation="border" size="sm" className="me-2" />
        )}
        {t('orderTasks.completeOrder')}
      </Button>
    </div>
  );
};

export default CloseOrderButton;
